<template>
  <div class="store-card" :class="['store-card-' + carrier]">
    <!-- 头部：品牌色条 + 门店编号 -->
    <div class="card-stripe" :class="carrier"></div>

    <div class="card-body">
      <!-- 第一行：名称 + 距离 -->
      <div class="head-row">
        <div class="name-block">
          <span class="carrier-pill" :class="carrier">
            {{ carrier === 'fedex' ? 'FedEx' : 'UPS' }}
          </span>
          <h3 class="store-name" :title="store.name">
            {{ store.name }}
          </h3>
        </div>
        <span class="distance-pill" v-if="store.distance">
          <i class="el-icon-position"></i>
          <strong>{{ store.distance }}</strong>
          <span class="unit">{{ store.distance_unit || 'mi' }}</span>
        </span>
      </div>

      <!-- 状态行（仅在没有营业时间数据时显示提示徽章） -->
      <div class="status-line">
        <span v-if="!hasHours" class="status-badge is-unknown">
          <span class="status-dot"></span>
          {{ $t('stores.noHours') }}
        </span>
        <span v-if="store.store_no" class="store-no">#{{ store.store_no }}</span>
      </div>

      <!-- 地址 -->
      <div class="info-row addr">
        <i class="el-icon-place"></i>
        <span :title="store.address">{{ store.address || '-' }}</span>
      </div>

      <!-- 电话 -->
      <div class="info-row phone" v-if="store.phone">
        <i class="el-icon-phone-outline"></i>
        <a :href="'tel:' + cleanPhone">{{ store.phone }}</a>
      </div>

      <!-- 业务范围 -->
      <div class="info-row services" v-if="services.length">
        <span
          v-for="(svc, idx) in displayServices"
          :key="idx"
          class="svc-tag"
          :class="carrier"
          :title="svc.label_en"
        >{{ svc.label_zh || svc.label_en }}</span>
        <el-popover
          v-if="services.length > maxVisibleServices"
          placement="top"
          trigger="hover"
          :title="$t('stores.services')"
          width="240"
        >
          <div class="more-services-pop">
            <span
              v-for="(svc, idx) in services.slice(maxVisibleServices)"
              :key="idx"
              class="svc-tag"
              :class="carrier"
            >{{ svc.label_zh || svc.label_en }}<span class="en"> ({{ svc.label_en }})</span></span>
          </div>
          <span slot="reference" class="more-tag">+{{ services.length - maxVisibleServices }}</span>
        </el-popover>
      </div>

      <!-- 一周营业时间（折叠） -->
      <div class="week-hours" v-if="hasHours && weekHoursList.length">
        <div class="week-toggle" @click="expanded = !expanded">
          <i :class="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          {{ $t('stores.weekHours') }}
        </div>
        <transition name="el-zoom-in-top">
          <ul v-if="expanded" class="week-list">
            <li
              v-for="(h, idx) in weekHoursList"
              :key="idx"
              :class="{ 'is-closed': h.closed }"
            >
              <span class="day">{{ h.day }}</span>
              <span class="time">
                {{ formatWeekDay(h) }}
              </span>
            </li>
          </ul>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
const MAX_VISIBLE = 3;

export default {
  name: 'StoreCard',
  props: {
    store: { type: Object, required: true },
    carrier: { type: String, default: 'fedex' },
  },
  data() {
    return {
      expanded: false,
      maxVisibleServices: MAX_VISIBLE,
    };
  },
  computed: {
    services() {
      return Array.isArray(this.store.services) ? this.store.services : [];
    },
    displayServices() {
      return this.services.slice(0, this.maxVisibleServices);
    },
    weekHoursList() {
      const arr = Array.isArray(this.store.hours) ? this.store.hours.slice() : [];
      arr.sort((a, b) => (a.order || 0) - (b.order || 0));
      return arr;
    },
    cleanPhone() {
      return String(this.store.phone || '').replace(/[^\d+]/g, '');
    },
    /**
     * 判断该店是否有任何营业时间数据
     * UPS 接口对司机投递点 / Drop Box / 私人入口等不返回 OperatingHours，
     * 这种情况下整周都是 closed:true 没有 open/close，要单独标识。
     */
    hasHours() {
      return this.weekHoursList.some(
        (h) => h.self_service || h.all_day || (!h.closed && h.open && h.close)
      );
    },
  },
  methods: {
    formatWeekDay(h) {
      if (h.self_service) return this.$t('stores.selfService247');
      if (h.all_day) return this.$t('stores.openAllDay');
      if (h.closed) return this.$t('stores.rest');
      return h.open + ' - ' + h.close;
    },
  },
};
</script>

<style lang="scss" scoped>
$fedex-color: #4d148c;
$fedex-color-2: #ff6600;
$ups-color: #351c15;
$ups-color-2: #ffb500;

.store-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  overflow: hidden;
  position: relative;
  font-size: 13px;
  color: #606266;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: transparent;
  }

  &.store-card-fedex:hover {
    box-shadow: 0 8px 24px rgba(77, 20, 140, 0.18);
  }
  &.store-card-ups:hover {
    box-shadow: 0 8px 24px rgba(255, 181, 0, 0.22);
  }
}

.card-stripe {
  width: 4px;
  flex-shrink: 0;

  &.fedex {
    background: linear-gradient(180deg, $fedex-color 0%, $fedex-color-2 100%);
  }
  &.ups {
    background: linear-gradient(180deg, $ups-color 0%, $ups-color-2 100%);
  }
}

.card-body {
  flex: 1;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

/* 第一行：名称 + 距离 */
.head-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.name-block {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
}

.carrier-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  flex-shrink: 0;

  &.fedex {
    background: $fedex-color;
    color: #fff;
  }
  &.ups {
    background: $ups-color;
    color: $ups-color-2;
  }
}

.store-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.distance-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  padding: 4px 10px;
  background: #ecf5ff;
  border-radius: 12px;
  color: #409eff;
  font-size: 12px;
  flex-shrink: 0;

  i {
    font-size: 12px;
    align-self: center;
  }

  strong {
    font-size: 13px;
    font-weight: 700;
  }

  .unit {
    font-size: 11px;
    opacity: 0.8;
  }
}

/* 状态行 */
.status-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 500;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &.is-unknown {
    background: rgba(64, 158, 255, 0.10);
    color: #5294e2;

    .status-dot {
      background: #5294e2;
    }
  }
}

.store-no {
  margin-left: auto;
  font-size: 11px;
  color: #c0c4cc;
  font-weight: 500;
}

/* 信息行 */
.info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.5;
  min-width: 0;
  width: 100%;

  > i {
    color: #909399;
    flex-shrink: 0;
    font-size: 14px;
    margin-top: 2px;
  }

  &.addr {
    > span {
      flex: 1;
      min-width: 0;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-break: break-word;
    }
  }

  a {
    color: #409eff;
    text-decoration: none;
    font-variant-numeric: tabular-nums;
    &:hover { text-decoration: underline; }
  }
}

.services {
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding-left: 0;

  > i {
    display: none;
  }
}

.svc-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;

  &.fedex {
    background: rgba(77, 20, 140, 0.08);
    color: $fedex-color;
  }
  &.ups {
    background: rgba(255, 181, 0, 0.16);
    color: #b07d00;
  }
}

.more-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  color: #909399;
  cursor: pointer;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    color: #409eff;
    border-color: #409eff;
    background: #ecf5ff;
  }
}

.more-services-pop {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  .en {
    color: #909399;
    font-size: 10px;
  }
}

/* 营业时间折叠 */
.week-hours {
  border-top: 1px dashed #ebeef5;
  padding-top: 8px;
  margin-top: 2px;
}

.week-toggle {
  font-size: 12px;
  color: #409eff;
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s;

  &:hover { opacity: 0.75; }
}

.week-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 6px;
  font-size: 12px;

  li {
    display: flex;
    justify-content: space-between;
    padding: 3px 4px;
    color: #606266;

    &.is-closed .time {
      color: #c0c4cc;
    }
  }
  .day { flex-shrink: 0; min-width: 36px; }
  .time { font-variant-numeric: tabular-nums; }
}
</style>
